import React, { useState } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ListMusic, Shuffle, X, ChevronUp, ChevronDown, Repeat, Repeat1 } from 'lucide-react';
import { usePlayer } from '@/contexts/PlayerContext';
import Marquee from 'react-fast-marquee';

function formatTime(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function AudioPlayer() {
  const {
    currentTrack, isPlaying, progress, duration, volume, queue, shuffle, repeat,
    togglePlay, seekTo, changeVolume, nextTrack, prevTrack,
    removeFromQueue, reorderQueue, clearQueue, toggleShuffle, toggleRepeat, playlist, playlistIndex,
    radioMode, radioShowName, radioMeta,
    isReconnecting, usingLowStream,
  } = usePlayer();
  const [showQueue, setShowQueue] = useState(false);
  const isLive = radioMode && currentTrack?.id === '__live__';

  if (!currentTrack) return null;

  const upcomingPlaylist = playlist.slice(playlistIndex + 1, playlistIndex + 6);

  return (
    <>
      {/* Queue Panel */}
      {showQueue && (
        <div data-testid="queue-panel" className="fixed bottom-[72px] right-4 w-80 max-h-[60vh] bg-[#171A21] border border-white/10 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-fade-in-up">
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <h3 className="text-white font-semibold text-sm" style={{ fontFamily: 'Clash Display, sans-serif' }}>Queue</h3>
            <div className="flex items-center gap-1">
              {queue.length > 0 && (
                <button data-testid="clear-queue-btn" onClick={clearQueue} className="text-xs text-[#94A3B8] hover:text-red-400 px-2 py-1 rounded hover:bg-white/5">Clear</button>
              )}
              <button onClick={() => setShowQueue(false)} className="p-1 rounded hover:bg-white/10 text-[#94A3B8]"><X size={16} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {/* Now Playing */}
            <div className="px-2 pt-1 pb-2">
              <p className="text-xs text-[#D4AF37] font-semibold uppercase tracking-wider mb-2">Now Playing</p>
              <div className="flex items-center gap-3 p-2 rounded-lg bg-[#D4AF37]/10">
                <div className="flex items-end gap-[2px] h-4 shrink-0">
                  <div className="w-[2px] bg-[#D4AF37] rounded-full equalizer-bar-1" />
                  <div className="w-[2px] bg-[#D4AF37] rounded-full equalizer-bar-2" />
                  <div className="w-[2px] bg-[#D4AF37] rounded-full equalizer-bar-3" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{currentTrack.title}</p>
                  <p className="text-[#94A3B8] text-xs truncate">{currentTrack.artist}</p>
                </div>
              </div>
            </div>

            {/* Manual Queue */}
            {queue.length > 0 && (
              <div className="px-2 pb-2">
                <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-wider mb-2">Next in Queue</p>
                {queue.map((track, i) => (
                  <div key={`q-${i}`} data-testid={`queue-item-${i}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 group">
                    <span className="text-xs text-[#94A3B8] w-4 text-center shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">{track.title}</p>
                      <p className="text-[#94A3B8] text-xs truncate">{track.artist}</p>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                      {i > 0 && (
                        <button data-testid={`queue-move-up-${i}`} onClick={() => reorderQueue(i, i - 1)} className="p-1 rounded hover:bg-white/10 text-[#94A3B8]"><ChevronUp size={12} /></button>
                      )}
                      {i < queue.length - 1 && (
                        <button data-testid={`queue-move-down-${i}`} onClick={() => reorderQueue(i, i + 1)} className="p-1 rounded hover:bg-white/10 text-[#94A3B8]"><ChevronDown size={12} /></button>
                      )}
                      <button onClick={() => removeFromQueue(i)} className="p-1 rounded hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400">
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Up Next from Playlist */}
            {upcomingPlaylist.length > 0 && (
              <div className="px-2 pb-2">
                <p className="text-xs text-[#94A3B8] font-semibold uppercase tracking-wider mb-2">Up Next</p>
                {upcomingPlaylist.map((track, i) => (
                  <div key={`up-${i}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                    <span className="text-xs text-[#94A3B8] w-4 text-center shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">{track.title}</p>
                      <p className="text-[#94A3B8] text-xs truncate">{track.artist}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {queue.length === 0 && upcomingPlaylist.length === 0 && (
              <p className="text-center text-[#94A3B8] text-sm py-6">Queue is empty</p>
            )}
          </div>
        </div>
      )}

      {/* Player Bar */}
      <div data-testid="audio-player" className="fixed bottom-0 left-0 w-full backdrop-blur-2xl bg-[#0F1115]/80 border-t border-white/10 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress bar — hidden for live stream */}
          {!isLive && (
            <div
              className="relative h-1 bg-white/10 cursor-pointer -mt-px"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                seekTo(pct * duration);
              }}
            >
              <div
                className="absolute left-0 top-0 h-full bg-[#D4AF37] rounded-r-full transition-all"
                style={{ width: duration ? `${(progress / duration) * 100}%` : '0%' }}
              />
            </div>
          )}

          <div className="flex items-center gap-4 py-3">
            {/* Track Info */}
            <div className="flex items-center gap-3 min-w-0 flex-1 sm:flex-none sm:w-72">
              {isLive && radioMeta?.cover_url ? (
                <img
                  src={`${radioMeta.cover_url}?${Math.floor(Date.now() / 10000)}`}
                  alt="Album art"
                  className="w-12 h-12 rounded-xl object-cover shrink-0"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-12 h-12 bg-[#171A21] rounded-xl flex items-center justify-center shrink-0 overflow-hidden">
                  {isPlaying ? (
                    <div className="flex items-end gap-[2px] h-5">
                      <div className="w-[3px] bg-[#D4AF37] rounded-full equalizer-bar-1" />
                      <div className="w-[3px] bg-[#D4AF37] rounded-full equalizer-bar-2" />
                      <div className="w-[3px] bg-[#D4AF37] rounded-full equalizer-bar-3" />
                    </div>
                  ) : (
                    <Play size={18} className="text-[#D4AF37]" />
                  )}
                </div>
              )}
              <div className="min-w-0">
                {radioMode && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full live-pulse" />
                    <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{radioShowName || 'Christovibes Radio'}</span>
                    {isReconnecting && (
                      <span data-testid="reconnecting-badge" className="text-[10px] font-semibold text-[#D4AF37] ml-1">· Reconnecting…</span>
                    )}
                    {usingLowStream && !isReconnecting && (
                      <span data-testid="low-stream-badge" className="text-[10px] text-[#94A3B8] ml-1">· Data-saver</span>
                    )}
                    {radioMeta?.listeners > 0 && (
                      <span className="text-[10px] text-[#94A3B8] ml-1">{radioMeta.listeners} listening</span>
                    )}
                  </div>
                )}
                <div className="overflow-hidden">
                  {currentTrack.title.length > 25 ? (
                    <Marquee gradient={false} speed={30}>
                      <span className="text-white text-sm font-medium pr-8">{currentTrack.title}</span>
                    </Marquee>
                  ) : (
                    <p className="text-white text-sm font-medium truncate">{currentTrack.title}</p>
                  )}
                </div>
                <p className="text-[#94A3B8] text-xs truncate">{currentTrack.artist}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {!isLive && (
                <button
                  data-testid="shuffle-btn"
                  onClick={toggleShuffle}
                  className={`p-2 rounded-full transition-colors hidden sm:block ${shuffle ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#94A3B8] hover:text-white hover:bg-white/10'}`}
                >
                  <Shuffle size={16} />
                </button>
              )}
              {!isLive && (
                <button data-testid="prev-track-btn" onClick={prevTrack} className="p-2 rounded-full hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors hidden sm:block">
                  <SkipBack size={18} />
                </button>
              )}
              <button
                data-testid="play-pause-btn"
                onClick={togglePlay}
                className="p-3 rounded-full bg-[#D4AF37] text-[#0F1115] hover:bg-[#E8C24D] transition-colors active:scale-95 shadow-lg shadow-[#D4AF37]/20"
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
              </button>
              {!isLive && (
                <button data-testid="next-track-btn" onClick={nextTrack} className="p-2 rounded-full hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors hidden sm:block">
                  <SkipForward size={18} />
                </button>
              )}
              {!isLive && (
                <>
                  <button
                    data-testid="repeat-btn"
                    onClick={toggleRepeat}
                    className={`p-2 rounded-full transition-colors hidden sm:block relative ${repeat !== 'off' ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#94A3B8] hover:text-white hover:bg-white/10'}`}
                    title={repeat === 'off' ? 'Repeat Off' : repeat === 'all' ? 'Repeat All' : 'Repeat One'}
                  >
                    {repeat === 'one' ? <Repeat1 size={16} /> : <Repeat size={16} />}
                  </button>
                  <button
                    data-testid="queue-toggle-btn"
                    onClick={() => setShowQueue(!showQueue)}
                    className={`p-2 rounded-full transition-colors hidden sm:block relative ${showQueue ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#94A3B8] hover:text-white hover:bg-white/10'}`}
                  >
                    <ListMusic size={16} />
                    {queue.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D4AF37] rounded-full text-[9px] font-bold text-[#0F1115] flex items-center justify-center">{queue.length}</span>
                    )}
                  </button>
                </>
              )}
            </div>

            {/* Time / LIVE info */}
            <div className="hidden md:flex items-center gap-2 text-xs font-mono flex-1 justify-center">
              {isLive ? (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-red-400 font-bold text-xs uppercase tracking-wider">
                    <span className="w-2 h-2 bg-red-500 rounded-full live-pulse" /> Live
                  </span>
                  {radioMeta?.next_title && (
                    <span className="text-[#94A3B8] font-sans text-xs">
                      Next: <span className="text-white/70">{radioMeta.next_artist} — {radioMeta.next_title}</span>
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-[#94A3B8]">{formatTime(progress)} / {formatTime(duration)}</span>
              )}
            </div>

            {/* Volume */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => changeVolume(volume > 0 ? 0 : 0.8)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors"
              >
                {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input
                data-testid="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={e => changeVolume(parseFloat(e.target.value))}
                className="w-20 accent-[#D4AF37] h-1"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
